/**
 * A1: installment schedule with paid/partial/unpaid chips (server-derived —
 * the client never computes retirement status).
 */

import React, { useEffect, useState } from 'react';
import { customerPortalApi, ScheduleResponse } from '../../../services/customerPortalApi';
import { PORTAL_COPY, PortalLanguage } from '../portalCopy';

interface Props {
  accountUuid: string;
  language?: PortalLanguage;
}

const STATUS_STYLES: Record<string, string> = {
  paid: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  unpaid: 'bg-gray-100 text-gray-800',
};

const PaymentScheduleCard: React.FC<Props> = ({ accountUuid, language = 'en' }) => {
  const copy = PORTAL_COPY[language];
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    customerPortalApi
      .getSchedule(accountUuid)
      .then(setData)
      .catch(() => setError(true));
  }, [accountUuid]);

  const statusLabel = (s: string) =>
    s === 'paid' ? copy.statusPaid : s === 'partial' ? copy.statusPartial : copy.statusUnpaid;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-base font-semibold text-gray-900">{copy.scheduleTitle}</h2>
      </div>
      {error && <div className="p-6 text-center text-gray-500">{copy.loadError}</div>}
      {!error && data && !data.has_schedule && (
        <div className="p-6 text-center text-gray-500">{copy.scheduleEmpty}</div>
      )}
      {!error && data && data.has_schedule && (
        <>
          {data.has_unallocated_payments && (
            <div className="mx-6 mt-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded-lg text-sm">
              {copy.scheduleCaution}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {copy.colPeriod}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {copy.colDueDate}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {copy.colAmount}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                    {copy.colPrincipal}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                    {copy.colInterest}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {copy.colStatus}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.schedule.map((row) => (
                  <tr key={row.period_number} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{row.period_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {new Date(`${row.due_date}T00:00:00`).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      ${row.payment.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                      ${row.principal.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                      ${row.interest.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[row.status]}`}
                      >
                        {statusLabel(row.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentScheduleCard;
